<template>
  <div id="app">
    <router-view v-if="!isConfiguratorLoading" :products="products" />

    <share-email />

    <b-modal
      v-if="$root.modals.maintenance"
      v-model="$root.modals.maintenance.active"
      class="scheduled-maintenance"
      title="Information"
      ok-title="Close"
      :ok-only="true"
      size="lg"
    >
      <p class="info-icon"><i class="fa fa-info-circle" /></p>
      <h3>Scheduled Maintenance Notification</h3>
      <p>Please be aware that <strong>C.H.I. HubX</strong>, <strong>DoorVisions</strong>, and
        <strong>HubX Mobile</strong> will be down for scheduled maintenance from <strong>10:00
          PM CT Friday, September 13th to 10:00 PM CT Saturday, September 14th.</strong></p>
      <p>You will be notified when services are fully restored. We apologize for any
        inconvenience this may cause and appreciate your understanding and cooperation.</p>
    </b-modal>

    <b-modal
      v-if="$root.modals.confirm"
      v-model="$root.modals.confirm.active"
      title="Confirm"
      ok-title="Yes"
      cancel-title="No"
      @ok="$root.modals.confirm.resolve"
      @cancel="$root.modals.confirm.reject"
    >
      {{ $root.modals.confirm.message }}
    </b-modal>

    <b-modal
      v-if="$root.modals.alert"
      v-model="$root.modals.alert.active"
      :title="$root.modals.alert.title"
      :ok-only="true"
      @ok="$root.modals.alert.resolve"
    >
      {{ $root.modals.alert.message }}
    </b-modal>

    <div class="loading-cover" v-if="$route.query.loading || isConfiguratorLoading">
      <spinner />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import Spinner from '@/components/utility/Spinner';
import ShareEmail from '@/components/share/Email';
import { receivePostMessage } from '@/helpers/embedded';

export default {
  name: 'app',
  data() {
    return {
      products: [],
      isConfiguratorLoading: false,
    };
  },
  computed: {
    ...mapState([
      'currentLayer',
    ]),
    ...mapGetters([
      'isConfigurator',
      'isCommercial',
      'isHub',
    ]),
  },
  methods: {
    ...mapMutations([
      'setCurrentProduct',
    ]),
    async loadProducts() {
      const dataSettings = { xml: `product${this.isCommercial ? '-commercial' : ''}`, dontLoadGrids: this.isHub || window.location.pathname.startsWith('/design-report') };
      await window.Renoworks.ProductController.load(dataSettings);
      this.products = window.Renoworks.ProductController.getAllProducts();
      return this.products;
    },
  },
  async created() {
    // this.$root.maintenance();
    const promisedProducts = this.loadProducts();

    if (this.isConfigurator) {
      if (window.parent !== window.self) {
        this.isConfiguratorLoading = true;

        receivePostMessage(async ({
          action,
          payload: { currentLayer = {}, currentProduct = {} } = {},
        }) => {
          if (action === 'load') {
            // restore app state

            // 'send' stored overrides to Configurator.created()'s new Layer()
            window.layerInitDefaults = currentLayer;

            // restore product settings, if specified
            const product = (await promisedProducts).find(
              p => p.name === currentProduct.name && p.rwd === currentProduct.rwd,
            );
            if (product) {
              product.selectByGridSettings(currentProduct.gridSettings);
              this.setCurrentProduct(product);
            }

            this.isConfiguratorLoading = false;
          }
        });
      } else {
        // eslint-disable-next-line no-console
        console.info('Skipping postMessage payload init since not in iframe');
      }
    }
  },
  components: {
    Spinner,
    ShareEmail,
  },
};
</script>

<style lang="scss">
@import "~@/assets/styles/variables.chi.scss";
@import "~bootstrap/scss/bootstrap";

// Bootstrap tweaks/fixes/overrides
.popover {
  // @HACK: to prevent hover flicker loop on small screens
  pointer-events: none;
}
.modal-xl {
  // adds new size to account for DealerLocator full-screen modal
  max-width: 96%;
}

// App
html,
body,
#app {
  height: 100%;
}

.flex-auto {
  flex: auto;
}
.flex-none {
  flex: none;
}
.flex-fill {
  @apply .flex-auto;

  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.overflow-ellipsis {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.btn-overlay {
  position: absolute;
  padding: $spacer / 4 $spacer / 2;
  z-index: 3;

  &:not(:hover) {
    background-color: $white;
    color: $chi-gray-darkest;
    border-color: currentColor;
  }

  &--top-left {
    top: $spacer / 4;
    left: $spacer / 4;
  }
  &--top-right {
    top: $spacer / 4;
    right: $spacer / 4;
  }
}

.info-button {
  display: inline-block;

  .fa {
    color: $chi-gray-darkest;
    font-size: 20px;
    vertical-align: middle;
  }
}

.toggle-ai:focus {
  outline: none;
  box-shadow: none;
}

.card {
  &.linked-card {
    position: relative;
    overflow: hidden;

    .card-img {
      max-width: 100%;
      min-height: 1px; // maintain aspect ratio on IE11
      text-align: center;

      &.card-header {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-grow: 1;

        + .card-body {
          flex-grow: 0;
        }
      }
    }

    .card-link,
    input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      cursor: pointer;
      z-index: 2;
    }

    input[type="file"] {
      font-size: 1000px; // IE11
      opacity: 0;
    }

    &:hover,
    &.active {
      color: $primary;
      border-color: currentColor;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.loading-cover {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 100;
}

.scheduled-maintenance {
  text-align: center;

  h3 {
    color: #c9213c;
    font-weight: 600;
    font-size: 1.4rem;
  }

  p {
    font-size: 0.9rem;
    line-height: 1.2rem;
  }

  .modal-header {
    height: 2.8rem;
    padding-top: 0.6rem;

    .modal-title {
      font-size: 1.2rem;
    }
  }

  .info-icon {
    font-size: 3rem;
    color: #23A2B8;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue